import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import TrainerSidebar from '../../components/ui/TrainerSidebar';
import SessionMonitorHeader from '../../components/ui/SessionMonitorHeader';
import ClientCard from './components/ClientCard';
import ClientTable from './components/ClientTable';
import ClientForm from './components/ClientForm';
import ClientFilters from './components/ClientFilters';
import ClientStats from './components/ClientStats';

const ClientManagement = () => {
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [viewMode, setViewMode] = useState('table'); // 'table' | 'cards'
  const [showForm, setShowForm] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    ageRange: '',
    lastTraining: '',
    activePlans: ''
  });
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');

  // Mock client data
  const [clients, setClients] = useState([
    {
      id: 1,
      name: "Margaret Chen",
      dateOfBirth: "1952-03-15",
      rfidId: "MC7829A4B1",
      textIdentifier: "Blue Card - Room 101",
      photo: "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=400&h=400&fit=crop&crop=face",
      status: "active",
      activePlans: 2,
      lastTraining: "2025-01-02T10:30:00Z",
      notes: "Prefers morning sessions. Has mild arthritis in left knee.",
      createdAt: "2024-11-15T09:00:00Z",
      updatedAt: "2025-01-02T10:30:00Z"
    },
    {
      id: 2,
      name: "Robert Williams",
      dateOfBirth: "1948-07-22",
      rfidId: "RW9384C7D2",
      textIdentifier: "Green Card - Wheelchair Access",
      photo: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&h=400&fit=crop&crop=face",
      status: "active",
      activePlans: 1,
      lastTraining: "2025-01-01T14:15:00Z",
      notes: "Uses wheelchair. Focus on upper body and core strength.",
      createdAt: "2024-10-20T11:30:00Z",
      updatedAt: "2025-01-01T14:15:00Z"
    },
    {
      id: 3,
      name: "Dorothy Martinez",
      dateOfBirth: "1955-11-08",
      rfidId: "DM5672E9F3",
      textIdentifier: "Red Card - Balance Training",
      photo: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=400&h=400&fit=crop&crop=face",
      status: "active",
      activePlans: 3,
      lastTraining: "2024-12-30T16:45:00Z",
      notes: "Excellent progress with balance exercises. Very motivated.",
      createdAt: "2024-09-10T08:15:00Z",
      updatedAt: "2024-12-30T16:45:00Z"
    },
    {
      id: 4,
      name: "James Thompson",
      dateOfBirth: "1950-05-14",
      rfidId: "JT8493A1B5",
      textIdentifier: "Yellow Card - Cardiac Rehab",
      photo: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop&crop=face",
      status: "inactive",
      activePlans: 0,
      lastTraining: "2024-12-15T11:20:00Z",
      notes: "Cardiac rehabilitation program. Monitor heart rate closely.",
      createdAt: "2024-08-05T13:45:00Z",
      updatedAt: "2024-12-20T09:30:00Z"
    },
    {
      id: 5,
      name: "Helen Rodriguez",
      dateOfBirth: "1953-09-30",
      rfidId: "HR7261D4E8",
      textIdentifier: "Purple Card - Diabetes Management",
      photo: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400&h=400&fit=crop&crop=face",
      status: "active",
      activePlans: 1,
      lastTraining: null,
      notes: "New client. Type 2 diabetes. Start with low-intensity exercises.",
      createdAt: "2025-01-01T10:00:00Z",
      updatedAt: "2025-01-01T10:00:00Z"
    },
    {
      id: 6,
      name: "Frank Anderson",
      dateOfBirth: "1947-12-03",
      rfidId: "FA3847B6C9",
      textIdentifier: "Orange Card - Post-Surgery",
      photo: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=400&h=400&fit=crop&crop=face",
      status: "suspended",
      activePlans: 0,
      lastTraining: "2024-11-28T09:15:00Z",
      notes: "Hip replacement recovery. Cleared for light exercises only.",
      createdAt: "2024-07-12T14:20:00Z",
      updatedAt: "2024-12-01T16:00:00Z"
    }
  ]);

  // Filter and sort clients
  const filteredAndSortedClients = useMemo(() => {
    let filtered = clients;

    // Apply search filter
    if (searchTerm) {
      const search = searchTerm?.toLowerCase();
      filtered = filtered?.filter(client =>
        client?.name?.toLowerCase()?.includes(search) ||
        client?.rfidId?.toLowerCase()?.includes(search) ||
        (client?.textIdentifier && client?.textIdentifier?.toLowerCase()?.includes(search))
      );
    }

    // Apply advanced filters
    if (filters?.status) {
      filtered = filtered?.filter(client => client?.status === filters?.status);
    }

    if (filters?.ageRange) {
      filtered = filtered?.filter(client => {
        let age = calculateAge(client?.dateOfBirth);
        switch (filters?.ageRange) {
          case '18-30': return age >= 18 && age <= 30;
          case '31-50': return age >= 31 && age <= 50;
          case '51-65': return age >= 51 && age <= 65;
          case '66-75': return age >= 66 && age <= 75;
          case '76+': return age >= 76;
          default: return true;
        }
      });
    }

    if (filters?.lastTraining) {
      const now = new Date();
      filtered = filtered?.filter(client => {
        if (filters?.lastTraining === 'never') {
          return !client?.lastTraining;
        }
        
        if (!client?.lastTraining) return false;
        
        const lastTraining = new Date(client.lastTraining);
        switch (filters?.lastTraining) {
          case 'today':
            return lastTraining?.toDateString() === now?.toDateString();
          case 'week':
            const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            return lastTraining >= weekAgo;
          case 'month':
            const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
            return lastTraining >= monthAgo;
          default:
            return true;
        }
      });
    }

    if (filters?.activePlans) {
      filtered = filtered?.filter(client => {
        const plans = client?.activePlans || 0;
        switch (filters?.activePlans) {
          case '0': return plans === 0;
          case '1': return plans === 1;
          case '2+': return plans >= 2;
          default: return true;
        }
      });
    }

    // Apply sorting
    filtered?.sort((a, b) => {
      let aValue, bValue;

      switch (sortField) {
        case 'name':
          aValue = a?.name?.toLowerCase();
          bValue = b?.name?.toLowerCase();
          break;
        case 'age':
          aValue = calculateAge(a?.dateOfBirth);
          bValue = calculateAge(b?.dateOfBirth);
          break;
        case 'rfidId':
          aValue = a?.rfidId;
          bValue = b?.rfidId;
          break;
        case 'status':
          aValue = a?.status;
          bValue = b?.status;
          break;
        case 'activePlans':
          aValue = a?.activePlans || 0;
          bValue = b?.activePlans || 0;
          break;
        case 'lastTraining':
          aValue = a?.lastTraining ? new Date(a.lastTraining)?.getTime() : 0;
          bValue = b?.lastTraining ? new Date(b.lastTraining)?.getTime() : 0;
          break;
        default:
          aValue = a?.[sortField];
          bValue = b?.[sortField];
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [clients, searchTerm, filters, sortField, sortDirection]);

  const calculateAge = (dateOfBirth) => {
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today?.getFullYear() - birthDate?.getFullYear();
    const monthDiff = today?.getMonth() - birthDate?.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today?.getDate() < birthDate?.getDate())) {
      age--;
    }
    
    return age;
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleAddClient = () => {
    setEditingClient(null);
    setShowForm(true);
  };

  const handleEditClient = (client) => {
    setEditingClient(client);
    setShowForm(true);
  };

  const handleSaveClient = (clientData) => {
    if (editingClient) {
      // Update existing client
      setClients(prev => prev?.map(client => 
        client?.id === editingClient?.id ? clientData : client
      ));
    } else {
      // Add new client
      setClients(prev => [...prev, clientData]);
    }
    
    setShowForm(false);
    setEditingClient(null);
  };

  const handleToggleStatus = (client) => {
    const newStatus = client?.status === 'active' ? 'inactive' : 'active';
    setClients(prev => prev?.map(c => 
      c?.id === client?.id 
        ? { ...c, status: newStatus, updatedAt: new Date()?.toISOString() }
        : c
    ));
  };

  const handleViewHistory = (client) => {
    navigate(`/client-evaluation?clientId=${client?.id}`);
  };

  const handleAssignPlan = (client) => {
    navigate(`/training-plan-builder?clientId=${client?.id}&action=assign`);
  };

  const handleViewEvaluation = (client) => {
    navigate(`/client-evaluation?clientId=${client?.id}&view=progress`);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setFilters({
      status: '',
      ageRange: '',
      lastTraining: '',
      activePlans: ''
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <TrainerSidebar 
        isCollapsed={sidebarCollapsed} 
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} 
      />
      <SessionMonitorHeader sidebarCollapsed={sidebarCollapsed} />
      <main 
        className={`
          pt-16 transition-all duration-300 ease-out
          ${sidebarCollapsed ? 'ml-20' : 'ml-80'}
          lg:${sidebarCollapsed ? 'ml-20' : 'ml-80'}
          md:${sidebarCollapsed ? 'ml-16' : 'ml-72'}
          sm:${sidebarCollapsed ? 'ml-16' : 'ml-64'}
        `}
      >
        <div className="p-6 space-y-6">
          {/* Page Header */}
          <div className="flex items-center justify-between">
            <div className='mt-5'>
              <h1 className="text-2xl font-bold text-foreground">Client Management</h1>
              <p className="text-muted-foreground">
                Manage elderly client profiles and training assignments
              </p>
            </div>
            
            <div className="flex items-center space-x-3">
              {/* View Mode Toggle */}
              <div className="flex items-center bg-muted rounded-lg p-1">
                <Button
                  variant={viewMode === 'table' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('table')}
                  iconName="Table"
                  iconSize={16}
                  className="px-3"
                />
                <Button
                  variant={viewMode === 'cards' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('cards')}
                  iconName="Grid3X3"
                  iconSize={16}
                  className="px-3"
                />
              </div>
              
              <Button
                onClick={handleAddClient}
                iconName="UserPlus"
                iconPosition="left"
                iconSize={20}
              >
                Add Client
              </Button>
            </div>
          </div>

          {/* Client Statistics */}
          <ClientStats clients={clients} />

          {/* Filters */}
          <ClientFilters
            onSearch={setSearchTerm}
            onFilterChange={setFilters}
            onClearFilters={handleClearFilters}
            totalClients={clients?.length}
            filteredCount={filteredAndSortedClients?.length}
          />

          {/* Client Form */}
          {showForm && (
            <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-6">
              <div className="w-full max-w-4xl max-h-[90vh] overflow-y-auto">
                <ClientForm
                  client={editingClient}
                  onSave={handleSaveClient}
                  onCancel={() => {
                    setShowForm(false);
                    setEditingClient(null);
                  }}
                  isEditing={!!editingClient}
                />
              </div>
            </div>
          )}

          {/* Client List */}
          {viewMode === 'table' ? (
            <ClientTable
              clients={filteredAndSortedClients}
              onEdit={handleEditClient}
              onViewHistory={handleViewHistory}
              onToggleStatus={handleToggleStatus}
              onAssignPlan={handleAssignPlan}
              onViewEvaluation={handleViewEvaluation}
              onSort={handleSort}
              sortField={sortField}
              sortDirection={sortDirection}
            />
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
              {filteredAndSortedClients?.map((client) => (
                <ClientCard
                  key={client?.id}
                  client={client}
                  onEdit={handleEditClient}
                  onViewHistory={handleViewHistory}
                  onToggleStatus={handleToggleStatus}
                  onAssignPlan={handleAssignPlan}
                  onViewEvaluation={handleViewEvaluation}
                />
              ))}
              
              {filteredAndSortedClients?.length === 0 && (
                <div className="col-span-full text-center py-12">
                  <Icon name="Users" size={48} className="text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-foreground mb-2">No clients found</h3>
                  <p className="text-muted-foreground mb-4">
                    {searchTerm || Object.values(filters)?.some(f => f) 
                      ? 'Try adjusting your search or filters' :'Add your first client to get started with training management'
                    }
                  </p>
                  {!searchTerm && !Object.values(filters)?.some(f => f) && (
                    <Button
                      onClick={handleAddClient}
                      iconName="UserPlus"
                      iconPosition="left"
                      iconSize={20}
                    >
                      Add First Client
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default ClientManagement;